import {AgentInformation, ValidAgent} from 'ApplicationEntry/models/AgentInformation';
import {ValidAgentList} from 'ApplicationEntry/models/AgentList';
import {UserAssignedAgentNumber} from 'ApplicationEntry/models/AgentNumber';
import {UserAssignedCaseNumber} from 'ApplicationEntry/models/CaseNumber';
import { isValidDY } from 'ApplicationEntry/models/Date';
import {UserAssignedFormNumber} from 'ApplicationEntry/models/FormNumber';
import {ValidGroupBillingAgent} from 'ApplicationEntry/models/GroupBillingAgent';
import {ValidOrdinaryBillingAgent} from 'ApplicationEntry/models/OrdinaryBillingAgent';
import {ValidZipCode} from 'ApplicationEntry/models/ZipCode';
import {NonEmptyString} from 'Core/models/EmptyString';
import * as I from 'immutable';
import DateUtility from 'Shared/utilities/DateUtility';

export const getBillingMode = (billingAgentBillingMode: string) => {
  let billingMode = billingAgentBillingMode;
  if (billingMode !== undefined) {
    billingMode = billingAgentBillingMode.trim();
    if (billingMode === 'MONTHLY') {
      billingMode = 'Monthly';
    } else if (billingMode === 'BI-WEEKLY') {
      billingMode = 'Bi-Weekly';
    } else if (billingMode === 'WEEKLY') {
      billingMode = 'Weekly';
    } else if (billingMode === 'QUARTERLY') {
      billingMode = 'Quarterly';
    } else if (billingMode === 'SEMI-MONTHLY') {
      billingMode = 'SemiMonthly';
    } else if (billingMode === 'SEMI-ANNUALLY') {
      billingMode = 'SemiAnnual';
    } else if (billingMode === 'NINTHLY') {
      billingMode = 'Ninthly';
    } else if (billingMode === 'TENTHLY') {
      billingMode = 'Tenthly';
    } else if (billingMode === 'THIRTEENTHLY') {
      billingMode = 'Thirteenthly';
    } else if (billingMode === '28DAY') {
      billingMode = '28day';
    } else if (billingMode === 'ANNUALLY') {
      billingMode = 'Annual';
    }
  }

  return billingMode;
};

const getPayroll = (
  payrollAllotment: boolean,
  caseNumber: string,
  billingTransitNumber: string,
  billingAccountNumber: string
) => {
  let payroll = payrollAllotment;
  if (payrollAllotment && caseNumber === '' && billingTransitNumber !== '' && billingAccountNumber !== '') {
    payroll = false;
  }
  return payroll;
};

export const getOrdinaryBillingAgent = (caseInformation: object, clientApplicant: object, policy: object, pacDataResultData: object) => {
  const billingMode = getBillingMode(caseInformation['billingMode']);
  const clientApplicantAddress = clientApplicant['address'];
  const formName = policy['form']['documentShortName'];
  const fipscoFormName = 'ABJ' + formName;

  return ValidOrdinaryBillingAgent.create(
    UserAssignedCaseNumber.create(caseInformation['caseNumber']),
    NonEmptyString.create(Number(caseInformation['payorNumber']) === 0 ? '' : caseInformation['payorNumber']),
    NonEmptyString.create(caseInformation['caseName']),
    NonEmptyString.create(caseInformation['billingTransitNumber']),
    NonEmptyString.create(caseInformation['billingAccountNumber']),
    NonEmptyString.create(caseInformation['billingDraftDay']),
    NonEmptyString.create(billingMode),
    NonEmptyString.create(caseInformation['achTransitNumber']),
    NonEmptyString.create(caseInformation['achAccountNumber']),
    NonEmptyString.create(caseInformation['achAccountType']),
    NonEmptyString.create(caseInformation['achPayerName']),
    NonEmptyString.create(caseInformation['achDraftDay']), // Convert NonEmptyString to Number
    new Date(DateUtility.toFormattedDateAsUtc(caseInformation['achAuthorizationDate'])),
    new Date(DateUtility.toFormattedDateAsUtc(caseInformation['requestedIssueDate'])),
    UserAssignedFormNumber.create(formName, formName, fipscoFormName, formName, fipscoFormName),
    NonEmptyString.create(caseInformation['payorNumber']),
    NonEmptyString.create(clientApplicantAddress['state']),
    ValidZipCode.create(clientApplicantAddress['zipCode']),
    NonEmptyString.create(caseInformation['situs']),
    new Date(DateUtility.toFormattedDateAsUtc(caseInformation['dateSigned'])),
    Boolean(caseInformation['billingInkAuthorization']),
    getPayroll(
      Boolean(caseInformation['payroll']),
      caseInformation['caseNumber'],
      caseInformation['billingTransitNumber'],
      caseInformation['billingAccountNumber']
    ),
    Boolean(caseInformation['directBill']),
    caseInformation['billingTransitNumber'] !== '' && caseInformation['billingAccountNumber'] !== '' ? true : false,
    false,
    NonEmptyString.create(caseInformation['billingCode']),
    NonEmptyString.create(pacDataResultData['transitNumber']),
    NonEmptyString.create(pacDataResultData['bankAccountNumber']),
    NonEmptyString.create(pacDataResultData['dueDate']),
    NonEmptyString.create(pacDataResultData['accountHolderName']),
    NonEmptyString.create(pacDataResultData['bankType']),
    new Date(DateUtility.toFormattedDateAsUtc(pacDataResultData['athorizedDate']))
  );
};

export const getGroupBillingAgent = (caseInformation: object, clientApplicant: object, policy: object, pacDataResultData: object) => {
  const clientApplicantAddress = clientApplicant['address'];
  const billingMode = getBillingMode(caseInformation['billingMode']);
  const formName = policy['form']['documentShortName'];
  const fipscoFormName = 'ABJ' + formName;

  return ValidGroupBillingAgent.create(
    NonEmptyString.create(billingMode),
    NonEmptyString.create(Number(caseInformation['payorNumber']) === 0 ? '' : caseInformation['payorNumber']),
    new Date(DateUtility.toFormattedDateAsUtc(caseInformation['requestedIssueDate'])),
    UserAssignedFormNumber.create(formName, formName, fipscoFormName, formName, fipscoFormName),
    new Date(DateUtility.toFormattedDateAsUtc(caseInformation['dateSigned'])),
    false,
    Boolean(caseInformation['billingInkAuthorization']),
    NonEmptyString.create(caseInformation['payorNumber']),
    NonEmptyString.create(caseInformation['situs']),
    ValidZipCode.create(clientApplicantAddress['zipCode']),
    Boolean(caseInformation['payrollAllotment']),
    Boolean(caseInformation['directBill']),
    NonEmptyString.create(caseInformation['achTransitNumber']),
    NonEmptyString.create(caseInformation['achAccountNumber']),
    NonEmptyString.create(caseInformation['achAccountType']),
    NonEmptyString.create(caseInformation['achPayerName']),
    NonEmptyString.create(caseInformation['achDraftDay']), // Convert NonEmptyString to Number
    new Date(DateUtility.toFormattedDateAsUtc(caseInformation['achAuthorizationDate'])),
    false,
    NonEmptyString.create(pacDataResultData['transitNumber']),
    NonEmptyString.create(pacDataResultData['bankAccountNumber']),
    NonEmptyString.create(pacDataResultData['dueDate']),
    NonEmptyString.create(pacDataResultData['accountHolderName']),
    NonEmptyString.create(pacDataResultData['bankType']),
    new Date(DateUtility.toFormattedDateAsUtc(pacDataResultData['authorizedDate'])),
  );
};

export const getAgentsList = (agents: object[]) => {
  let agentsList = I.List<AgentInformation>();
  if (agents.length > 0) {
    agents.forEach(item => {
      const agentInfo: AgentInformation = ValidAgent.create(
        UserAssignedAgentNumber.create(item['ahlAgentCode']),
        NonEmptyString.create(item['name']),
        item['percentage'],
        item['agentSigned'],
        item['allstateAffiliated']
      );

      agentsList = agentsList.push(agentInfo);
    });
  }
  return ValidAgentList.create(agentsList);
};
